import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App as AntApp, ConfigProvider } from 'antd';
import ruRU from 'antd/locale/ru_RU';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import { useMemo, type JSX } from 'react';
import { createBrowserRouter, Navigate, RouterProvider, type RouteObject } from 'react-router';
import './i18n';
import { AuthProvider, RequireAuth, useAuth, type ShellMode } from './auth/AuthContext';
import { AppShell } from './components/AppShell';
import { BusinessContextProvider } from './context/BusinessContext';
import { StoreContextProvider } from './context/StoreContext';
import { ApprovalsPage } from './pages/ApprovalsPage';
import { BusinessesPage } from './pages/BusinessesPage';
import { DashboardPage } from './pages/DashboardPage';
import { DirectoryListPage } from './pages/DirectoryListPage';
import { LedgerPage } from './pages/LedgerPage';
import { LoginPage } from './pages/LoginPage';
import { MorePage } from './pages/MorePage';
import { MovementsPage } from './pages/MovementsPage';
import { OperationsPage } from './pages/OperationsPage';
import { ProductsPage } from './pages/ProductsPage';
import { ReportsPage } from './pages/ReportsPage';
import { SellerHomePage } from './pages/SellerHomePage';
import { SettingsPage } from './pages/SettingsPage';
import { SummaryPage } from './pages/SummaryPage';
import { ManualShipmentCreatePage } from './pages/ManualShipmentEditor';
import { ShipmentDetailPage } from './pages/ShipmentDetailPage';
import { ShipmentsPage } from './pages/ShipmentsPage';

dayjs.locale('ru');

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 15_000,
    },
  },
});

interface RouteFlags {
  mode: ShellMode;
  isStoreRestricted: boolean;
  isPlatformAdmin: boolean;
}

/** Role-aware child routes. A SELLER only ever reaches their own store and the
 * накладные they price; every admin route redirects them home. */
function childRoutes({ mode, isStoreRestricted, isPlatformAdmin }: RouteFlags): RouteObject[] {
  if (mode === 'platform-no-business') {
    return [
      { path: '/businesses', element: <BusinessesPage /> },
      { path: '*', element: <Navigate to="/businesses" replace /> },
    ];
  }
  if (mode === 'seller') {
    return [
      { path: '/', element: <SellerHomePage /> },
      { path: '/shipments', element: <ShipmentsPage /> },
      { path: '/shipments/:id', element: <ShipmentDetailPage /> },
      { path: '*', element: <Navigate to="/" replace /> },
    ];
  }
  return [
    { path: '/', element: <DashboardPage /> },
    { path: '/stores', element: <DirectoryListPage kind="store" /> },
    { path: '/stores/:id', element: <LedgerPage kind="store" /> },
    // Sklad pages are whole-business (supplier debt) — not for a store-restricted USER.
    ...(isStoreRestricted
      ? []
      : [
          { path: '/sklads', element: <DirectoryListPage kind="sklad" /> },
          { path: '/sklads/:id', element: <LedgerPage kind="sklad" /> },
        ]),
    { path: '/operations', element: <OperationsPage /> },
    { path: '/shipments', element: <ShipmentsPage /> },
    { path: '/shipments/new', element: <ManualShipmentCreatePage /> },
    { path: '/shipments/:id', element: <ShipmentDetailPage /> },
    { path: '/movements', element: <MovementsPage /> },
    { path: '/reports', element: <ReportsPage /> },
    { path: '/products', element: <ProductsPage /> },
    { path: '/summary', element: <SummaryPage /> },
    { path: '/approvals', element: <ApprovalsPage /> },
    { path: '/more', element: <MorePage /> },
    { path: '/settings', element: <SettingsPage /> },
    ...(isPlatformAdmin ? [{ path: '/businesses', element: <BusinessesPage /> }] : []),
    { path: '*', element: <Navigate to="/" replace /> },
  ];
}

/** Build the data router. Uses `createBrowserRouter` (not `<BrowserRouter>`) so
 * the full-page editors can block navigation on unsaved changes via `useBlocker`
 * — which only works inside a data router. */
function buildRouter(flags: RouteFlags): ReturnType<typeof createBrowserRouter> {
  return createBrowserRouter([
    { path: '/login', element: <LoginPage /> },
    {
      element: (
        <RequireAuth>
          <BusinessContextProvider>
            <StoreContextProvider>
              <AppShell />
            </StoreContextProvider>
          </BusinessContextProvider>
        </RequireAuth>
      ),
      children: childRoutes(flags),
    },
  ]);
}

/** Router lives under AuthProvider so it can read the role. Rebuilt only when
 * the role / business-selection flips (login, logout, business switch) — an
 * acceptable full remount. */
function AppRouter(): JSX.Element {
  const { isStoreRestricted, isPlatformAdmin, shellMode: mode } = useAuth();
  const router = useMemo(
    () => buildRouter({ mode, isStoreRestricted, isPlatformAdmin }),
    [mode, isStoreRestricted, isPlatformAdmin],
  );
  return <RouterProvider router={router} />;
}

export function App(): JSX.Element {
  return (
    <ConfigProvider
      locale={ruRU}
      // Render dropdowns (Select, DatePicker, Dropdown…) into their trigger's
      // parent instead of <body>, so the floating layer scrolls together with
      // the content. Without this, the desktop shell scrolls inside its own
      // Content box while a body-mounted dropdown stays put — opening a Select
      // and scrolling ran the page away under it.
      getPopupContainer={(node) => node?.parentElement ?? document.body}
      theme={{
        token: {
          colorPrimary: '#0e7490',
          borderRadius: 10,
          // Large touch targets — the owner drives this app with a thumb.
          controlHeight: 44,
          fontSize: 15,
        },
        components: {
          Menu: { itemHeight: 44 },
          // Compact calendar panel: with controlHeight 44 the default panel
          // grows past 440px and cannot fit above OR below the date input on
          // short phone screens — the month/year header ended up off-screen.
          DatePicker: { cellHeight: 22, cellWidth: 34, textHeight: 32 },
        },
      }}
    >
      <AntApp>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <AppRouter />
          </AuthProvider>
        </QueryClientProvider>
      </AntApp>
    </ConfigProvider>
  );
}
